import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from "@tanstack/react-query";
import { throwResponse, axiosAuthen, axiosCentral } from "config/axios";
import { IUser } from "service/administrator/interface";
import { ILogin, IMenuClickRecord } from "./interface";
import { IProperty } from "service/property/interface";
import { Navigate } from "react-router-dom";

export const useGetMe = (isPublic: boolean): UseQueryResult<IUser> => {
  return useQuery(
    ["get-me", isPublic],
    async () => {
      const res = await axiosAuthen.get("/auth/administrator/me");
      if (res.status === 200) {
        return res.data.data;
      }
      throwResponse(res);
    },
    { enabled: !isPublic }
  );
};

export const useMutationGetMe = (): UseMutationResult<IUser, Error> => {
  return useMutation(async () => {
    const res = await axiosAuthen.get("/administrator/me");
    if (res.status === 200) {
      return res.data.data;
    }
    throwResponse(res);
  });
};

export const useLogin = (): UseMutationResult<ILogin> => {
  return useMutation(async (data) => {
    const res = await axiosAuthen.post("/auth/web-login", data);
    if (res.status === 200) {
      return res.data.data;
    }
    throwResponse(res);
  });
};

export const Logout = () => {
  removeUuid();
  removeToken();
  removeProperty();
  removeProfile();
  window.location.reload();
};

export const refreshToken = async () => {
  const data = getProfile();
  const refreshToken = {
    refreshToken: data?.refreshToken,
  };
  const res = await axiosAuthen.post("/auth/refresh-token", refreshToken);
  if (res.status === 200) {
    setToken(res.data.accessToken);
    setProfile(res.data);
  } else {
    Logout();
  }
};

export const useLogout = (): UseMutationResult<unknown, Error> => {
  return useMutation(async (data) => {
    const res = await axiosAuthen.post("/auth/logout", data);
    if (res.status === 200) {
      return res.data;
    }
    throwResponse(res);
  });
};

export const usePostMenuClick = (): UseMutationResult<unknown, Error> => {
  return useMutation(async ({ ...data }: any) => {
    const res = await axiosCentral.post("/menu-records", data);
    if (res.status === 200) {
      return res.data.data;
    }
    throwResponse(res);
  });
};

export const removeToken = () => {
  localStorage.removeItem("lpp-access-token");
};

export const setToken = (token: string) => {
  localStorage.setItem("lpp-access-token", token);
};

export const getToken = (): string | null => {
  return localStorage.getItem("lpp-access-token");
};

export const removeUuid = () => {
  localStorage.removeItem("lpp-uuid-property");
};

export const setUuid = (uuid: string) => {
  localStorage.setItem("lpp-uuid-property", uuid);
};

export const getUuid = (): string | null => {
  return localStorage.getItem("lpp-uuid-property");
};

export const removeMenuCode = () => {
  localStorage.removeItem("lpp-menu-code");
};

export const setMenuCode = (MenuCode: string) => {
  localStorage.setItem("lpp-menu-code", MenuCode);
};

export const getMenuCode = (): string | null => {
  return localStorage.getItem("lpp-menu-code");
};

export const removeMenuSlug = () => {
  localStorage.removeItem("lpp-menu-slug");
};

export const setMenuSlug = (MenuCode: string) => {
  localStorage.setItem("lpp-menu-slug", MenuCode);
};

export const getMenuSlug = (): string | null => {
  return localStorage.getItem("lpp-menu-slug");
};

export const removeProperty = () => {
  const propertyId = window.location.hash.split("/")[2];
  localStorage.removeItem(`lpp-property-${propertyId}`);
};

export const setProperty = (value: IProperty, key: string) => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const getProperty = (): IProperty | null => {
  const propertyId = window.location.hash.split("/")[2];

  const value = localStorage.getItem(`lpp-property-${propertyId}`);
  if (value !== null) {
    return JSON.parse(value);
  }
  return value;
};

export const removeMenuClick = () => {
  localStorage.removeItem("lpp-action-menu-code");
};

export const setMenuClick = (value: IMenuClickRecord) => {
  localStorage.setItem("lpp-action-menu-code", JSON.stringify(value));
};

export const getMenuClick = (): IMenuClickRecord | null => {
  const value = localStorage.getItem("lpp-action-menu-code");
  if (value !== null) {
    return JSON.parse(value);
  }
  return value;
};

export const removeProfile = () => {
  localStorage.removeItem("lpp-profile-token");
};

export const setProfile = (token: ILogin) => {
  localStorage.setItem("lpp-profile-token", JSON.stringify(token));
};

export const getProfile = (): ILogin | null => {
  const value = localStorage.getItem("lpp-profile-token");
  if (value !== null) {
    return JSON.parse(value);
  }
  return value;
};
